import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import { unstable_useId as useId } from '@mui/material/utils';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import moment, { Moment } from 'moment';
import {
  MAX_SNOOZE_DAYS,
  MAX_SNOOZE_MS,
  SnoozePreset,
  snoozePresets,
  snoozeTimeError,
} from './snooze';

export interface SnoozeFailure {
  message: string;
  /** The backend refused the time itself, so picking another one can fix it. */
  invalidTime: boolean;
}

// The part of the time each label does not already say.
const PRESET_HINT: Record<SnoozePreset['key'], string> = {
  later: 'h:mm A',
  tomorrow: 'ddd h:mm A',
  monday: 'MMM D, h:mm A',
};

interface ISnoozeButtonProps {
  /** Resolves to why the snooze did not take, else null. */
  onSnooze: (until: string) => Promise<SnoozeFailure | null>;
  disabled?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * Snooze with Sam's presets (later today / tomorrow 9 AM / Monday 9 AM) or a
 * picked time, all read in the browser's zone. A preset whose time the
 * backend refuses reopens here as a custom time with the reason inline, so
 * there is one place a rep fixes it.
 *
 * Callers that reuse one instance across families must key it by family:
 * a result landing after the selection moved on is dropped with the
 * instance, so it can never open a retry bound to another family.
 */
export const SnoozeButton: React.FC<ISnoozeButtonProps> = ({
  onSnooze,
  disabled,
  sx,
}) => {
  const buttonId = useId();
  const menuId = useId();
  const titleId = useId();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [now, setNow] = useState<Moment>(() => moment());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [custom, setCustom] = useState<Moment | null>(null);
  // Shown only after a submit, so the dialog does not open on an error.
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const liveRef = useRef(true);
  useEffect(
    () => () => {
      liveRef.current = false;
    },
    [],
  );

  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    // The presets are relative to when the menu opens, not when it mounted.
    setNow(moment());
    setAnchor(event.currentTarget);
  };

  const openCustom = (initial: Moment, message: string | null = null) => {
    setCustom(initial);
    setError(message);
    setDialogOpen(true);
  };

  const choosePreset = async (until: Moment) => {
    setAnchor(null);
    const failure = await onSnooze(until.toISOString());
    if (!failure || !liveRef.current) return;
    if (failure.invalidTime) openCustom(until, failure.message);
    else toast.error(failure.message);
  };

  const submitCustom = async () => {
    const invalid = snoozeTimeError(custom, moment());
    if (invalid || !custom) {
      setError(invalid);
      return;
    }
    setSubmitting(true);
    const failure = await onSnooze(custom.toISOString());
    if (!liveRef.current) return;
    setSubmitting(false);
    if (failure) setError(failure.message);
    else setDialogOpen(false);
  };

  return (
    <>
      <Button
        id={buttonId}
        size="small"
        disabled={disabled}
        aria-haspopup="menu"
        aria-controls={anchor ? menuId : undefined}
        aria-expanded={anchor ? 'true' : undefined}
        onClick={openMenu}
        sx={sx}
      >
        Snooze
      </Button>
      <Menu
        id={menuId}
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        onClick={(event) => event.stopPropagation()}
        MenuListProps={{ 'aria-labelledby': buttonId, dense: true }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: '248px', borderRadius: '10px' } }}
      >
        {/* A subheader, not plain text: MenuList skips it, so the first
            preset takes focus and the arrow keys work. */}
        <ListSubheader
          sx={{
            lineHeight: '32px',
            fontSize: '13px',
            fontWeight: 600,
            color: '#7D818C',
          }}
        >
          Snooze until
        </ListSubheader>
        {snoozePresets(now).map((preset) => (
          <MenuItem
            key={preset.key}
            onClick={() => void choosePreset(preset.until)}
            sx={{ gap: 3 }}
          >
            <ListItemText
              primaryTypographyProps={{
                sx: { fontSize: '14px', fontWeight: 600, color: '#213B54' },
              }}
            >
              {preset.label}
            </ListItemText>
            <Typography sx={{ fontSize: '13px', color: '#7D818C' }}>
              {preset.until.format(PRESET_HINT[preset.key])}
            </Typography>
          </MenuItem>
        ))}
        <MenuItem
          onClick={() => {
            setAnchor(null);
            openCustom(moment().add(1, 'day').startOf('day').hour(9));
          }}
        >
          <ListItemText
            primaryTypographyProps={{
              sx: { fontSize: '14px', fontWeight: 600, color: '#213B54' },
            }}
          >
            Pick a date and time…
          </ListItemText>
        </MenuItem>
      </Menu>

      <Dialog
        open={dialogOpen}
        onClose={() => !submitting && setDialogOpen(false)}
        onClick={(event) => event.stopPropagation()}
        aria-labelledby={titleId}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle id={titleId} sx={{ color: '#213B54', fontWeight: 600 }}>
          Snooze until
        </DialogTitle>
        <DialogContent>
          <DateTimePicker
            value={custom}
            onChange={(value: Moment | null) => {
              setCustom(value);
              setError(null);
            }}
            disablePast
            maxDateTime={moment(Date.now() + MAX_SNOOZE_MS)}
            slotProps={{
              textField: {
                fullWidth: true,
                error: !!error,
                helperText:
                  error ||
                  `Returns to Open then, or sooner if the family or a contact replies. Up to ${MAX_SNOOZE_DAYS} days.`,
                sx: { marginTop: '8px' },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ padding: '0 24px 16px' }}>
          <Button
            onClick={() => setDialogOpen(false)}
            disabled={submitting}
            sx={{ color: '#7D818C', textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void submitCustom()}
            disabled={submitting || disabled}
            sx={{
              backgroundColor: '#213B54',
              textTransform: 'none',
              fontWeight: 600,
              ':hover': { backgroundColor: '#1A2F43' },
            }}
          >
            {submitting ? 'Snoozing…' : 'Snooze'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
